import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Users, Search, Shield, Edit, Plus, Trash2, UserPlus, UserMinus,
  Lock, Unlock, Key, History, Download, Upload, MoreVertical,
  Building2, ShieldCheck, Eye, ClipboardList, Settings2, ChevronRight,
  AlertTriangle, CheckCircle, XCircle, Clock, UserCog, RefreshCw, Copy
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const ROLES = [
  { value: 'admin', label: 'Administrator', color: 'bg-red-900/30 text-red-400 border-red-900/50' },
  { value: 'safety_manager', label: 'Safety Manager', color: 'bg-violet-900/30 text-violet-400 border-violet-900/50' },
  { value: 'safety_officer', label: 'Safety Officer', color: 'bg-sky-900/30 text-sky-400 border-sky-900/50' },
  { value: 'investigator', label: 'Investigator', color: 'bg-orange-900/30 text-orange-400 border-orange-900/50' },
  { value: 'auditor', label: 'Auditor', color: 'bg-emerald-900/30 text-emerald-400 border-emerald-900/50' },
  { value: 'trainer', label: 'Trainer', color: 'bg-yellow-900/30 text-yellow-400 border-yellow-900/50' },
  { value: 'inspector', label: 'Inspector', color: 'bg-slate-700 text-slate-300 border-slate-600' },
  { value: 'employee', label: 'Employee', color: 'bg-slate-800 text-slate-400 border-slate-700' },
];

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-900/30 text-emerald-400 border-emerald-800', icon: CheckCircle },
  inactive: { label: 'Inactive', color: 'bg-slate-800 text-slate-400 border-slate-700', icon: XCircle },
  suspended: { label: 'Suspended', color: 'bg-red-900/30 text-red-400 border-red-800', icon: AlertTriangle },
  pending_activation: { label: 'Pending', color: 'bg-amber-900/30 text-amber-400 border-amber-800', icon: Clock },
  locked: { label: 'Locked', color: 'bg-red-900/30 text-red-400 border-red-800', icon: Lock },
};

const PERMISSION_MODULES = [
  'incidents', 'risks', 'audits', 'capas', 'training', 'compliance',
  'documents', 'reports', 'workflows', 'users', 'organizations', 'settings',
  'meetings', 'safety_bulletins', 'bowtie', 'dashboard'
];

const PERMISSION_ACTIONS = ['view', 'create', 'edit', 'delete', 'approve', 'export', 'assign', 'close'];

export default function EnterpriseUserManagementPage() {
  const { t } = useTranslation();
  const { api, user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userFilter, setUserFilter] = useState({ status: '', role: '' });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(null);
  
  // Security Groups state
  const [securityGroups, setSecurityGroups] = useState([]);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(null);
  const [showGroupMembers, setShowGroupMembers] = useState(null);
  
  // Permission Sets state
  const [permissionSets, setPermissionSets] = useState([]);
  const [showCreatePermSet, setShowCreatePermSet] = useState(false);
  const [showEditPermSet, setShowEditPermSet] = useState(null);
  const [showViewPermSet, setShowViewPermSet] = useState(null);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkAssignForm, setBulkAssignForm] = useState({
    permission_set_ids: [], user_ids: [], mode: 'add', userSearch: ''
  });
  const [bulkPreview, setBulkPreview] = useState(null);        // preview payload from backend
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [showBulkHistory, setShowBulkHistory] = useState(false);
  const [bulkHistory, setBulkHistory] = useState([]);
  const [bulkHistoryLoading, setBulkHistoryLoading] = useState(false);
  const [expandedBatchId, setExpandedBatchId] = useState(null);
  
  // Audit Log state
  const [auditLogs, setAuditLogs] = useState([]);
  
  // Stats
  const [stats, setStats] = useState(null);

  // Form states
  const [newUser, setNewUser] = useState({
    email: '', name: '', employee_id: '', job_title: '', department: '',
    phone: '', mobile: '', roles: ['employee'], send_invite: true
  });
  const [newGroup, setNewGroup] = useState({ name: '', description: '', permission_set_ids: [] });
  const [newPermSet, setNewPermSet] = useState({ name: '', description: '', permissions: [] });

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!api) return;
    setLoading(true);
    try {
      const [usersRes, groupsRes, permSetsRes, statsRes] = await Promise.all([
        api.get('/user-management/users'),
        api.get('/user-management/security-groups'),
        api.get('/user-management/permission-sets'),
        api.get('/user-management/stats'),
      ]);
      setUsers(usersRes.data || []);
      setSecurityGroups(groupsRes.data || []);
      setPermissionSets(permSetsRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load user management data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const [auditActorOnly, setAuditActorOnly] = useState(false);

  const fetchAuditLog = useCallback(async () => {
    if (!api) return;
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (auditActorOnly && currentUser?.id) params.set('actor_id', currentUser.id);
      const res = await api.get(`/user-management/audit-log?${params.toString()}`);
      setAuditLogs(res.data || []);
    } catch (error) {
      console.error('Error fetching audit log:', error);
    }
  }, [api, auditActorOnly, currentUser]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditLog();
    }
  }, [activeTab, fetchAuditLog]);

  const handleSyncFromRoles = async () => {
    try {
      const res = await api.post('/user-management/security-groups/sync-from-roles');
      const s = res.data || {};
      toast.success(
        `Sync complete — scanned ${s.scanned}, changed ${s.users_changed} (+${s.total_added} / −${s.total_removed})`
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync groups from roles');
    }
  };

  const openBulkAssign = () => {
    setBulkAssignForm({ permission_set_ids: [], user_ids: [], mode: 'add', userSearch: '' });
    setBulkPreview(null);
    setShowBulkAssign(true);
  };

  const handleFetchBulkPreview = async () => {
    const { permission_set_ids, user_ids, mode } = bulkAssignForm;
    if (permission_set_ids.length === 0 || user_ids.length === 0) {
      toast.error('Pick at least one permission set and one user');
      return;
    }
    setBulkPreviewLoading(true);
    try {
      const res = await api.post('/user-management/permission-sets/bulk-assign/preview', {
        permission_set_ids, user_ids, mode,
      });
      setBulkPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview failed');
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const handleBulkAssign = async () => {
    const { permission_set_ids, user_ids, mode } = bulkAssignForm;
    if (permission_set_ids.length === 0 || user_ids.length === 0) {
      toast.error('Pick at least one permission set and one user');
      return;
    }
    try {
      const res = await api.post('/user-management/permission-sets/bulk-assign', {
        permission_set_ids, user_ids, mode,
      });
      const s = res.data || {};
      toast.success(
        `Assigned — ${s.updated} updated, ${s.unchanged} unchanged, ${s.not_found} not found`
      );
      setShowBulkAssign(false);
      setBulkPreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk assign failed');
    }
  };

  const openBulkHistory = async () => {
    setShowBulkHistory(true);
    setBulkHistoryLoading(true);
    setExpandedBatchId(null);
    try {
      const res = await api.get('/user-management/permission-sets/bulk-assign/history?limit=50');
      setBulkHistory(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch history');
    } finally {
      setBulkHistoryLoading(false);
    }
  };

  const handleFlipBatch = async (batchId, kind) => {
    const verb = kind === 'redo' ? 'Redo' : 'Undo';
    if (!confirm(`${verb} this bulk assign? Each affected user will be flipped ${kind === 'redo' ? 'back to the assigned state' : 'to their previous permission-set list'} — but only if their state hasn't been changed by someone else since.`)) return;
    try {
      const res = await api.post(`/user-management/permission-sets/bulk-assign/history/${batchId}/${kind}`);
      const c = res.data?.counts || {};
      toast.success(
        `${verb} complete — restored ${c.restored || 0}, already matches ${c.already_matches || 0}, skipped ${c.skipped_modified || 0}, not found ${c.not_found || 0}`
      );
      openBulkHistory();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `${verb} failed`);
    }
  };

  // User operations
  const handleCreateUser = async () => {
    try {
      await api.post('/user-management/users', newUser);
      toast.success('User created successfully');
      setShowCreateUser(false);
      setNewUser({ email: '', name: '', employee_id: '', job_title: '', department: '', phone: '', mobile: '', roles: ['employee'], send_invite: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (userId, data) => {
    try {
      await api.put(`/user-management/users/${userId}`, data);
      toast.success('User updated successfully');
      setShowEditUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleUserAction = async (userId, action, params = {}) => {
    try {
      if (action === 'activate') {
        await api.post(`/user-management/users/${userId}/activate`);
        toast.success('User activated');
      } else if (action === 'deactivate') {
        await api.post(`/user-management/users/${userId}/deactivate`);
        toast.success('User deactivated');
      } else if (action === 'suspend') {
        await api.post(`/user-management/users/${userId}/suspend?reason=${params.reason || 'Admin action'}`);
        toast.success('User suspended');
      } else if (action === 'unlock') {
        await api.post(`/user-management/users/${userId}/unlock`);
        toast.success('User unlocked');
      } else if (action === 'reset-password') {
        const res = await api.post(`/user-management/users/${userId}/reset-password`);
        toast.success(`Password reset. Temporary: ${res.data.temporary_password}`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const handleBulkAction = async (operation, params = {}) => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    try {
      await api.post('/user-management/bulk/users', {
        user_ids: selectedUsers,
        operation,
        params
      });
      toast.success(`Bulk ${operation} completed`);
      setSelectedUsers([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk operation failed');
    }
  };

  // Security Group operations
  const handleCreateGroup = async () => {
    try {
      await api.post('/user-management/security-groups', newGroup);
      toast.success('Security group created');
      setShowCreateGroup(false);
      setNewGroup({ name: '', description: '', permission_set_ids: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    }
  };

  const handleUpdateGroup = async (groupId, data) => {
    try {
      await api.put(`/user-management/security-groups/${groupId}`, data);
      toast.success('Security group updated');
      setShowEditGroup(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update group');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm('Are you sure you want to delete this security group?')) return;
    try {
      await api.delete(`/user-management/security-groups/${groupId}`);
      toast.success('Security group deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete group');
    }
  };

  const handleAddGroupMember = async (groupId, userId) => {
    try {
      await api.post(`/user-management/security-groups/${groupId}/members?user_id=${userId}`);
      toast.success('Member added to group');
      fetchData();
      // Refresh group members
      if (showGroupMembers) {
        const res = await api.get(`/user-management/security-groups/${groupId}`);
        setShowGroupMembers(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveGroupMember = async (groupId, userId) => {
    try {
      await api.delete(`/user-management/security-groups/${groupId}/members/${userId}`);
      toast.success('Member removed from group');
      fetchData();
      if (showGroupMembers) {
        const res = await api.get(`/user-management/security-groups/${groupId}`);
        setShowGroupMembers(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  // Permission Set operations
  const handleCreatePermSet = async () => {
    try {
      await api.post('/user-management/permission-sets', newPermSet);
      toast.success('Permission set created');
      setShowCreatePermSet(false);
      setNewPermSet({ name: '', description: '', permissions: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create permission set');
    }
  };

  const handleDeletePermSet = async (setId) => {
    if (!confirm('Are you sure you want to delete this permission set?')) return;
    try {
      await api.delete(`/user-management/permission-sets/${setId}`);
      toast.success('Permission set deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete permission set');
    }
  };

  // Save changes to a Permission Set's name / description. Permissions and
  // is_system remain read-only from this dialog by design (option B — simpler
  // metadata-only edit). Backend PUT rejects is_system sets with 400 so a
  // race-condition on a system record fails safely.
  const [editPermSetForm, setEditPermSetForm] = useState({ name: '', description: '' });
  const openEditPermSet = (ps) => {
    setEditPermSetForm({ name: ps.name || '', description: ps.description || '' });
    setShowEditPermSet(ps);
  };
  const handleSavePermSetEdit = async () => {
    if (!showEditPermSet) return;
    const trimmed = editPermSetForm.name.trim();
    if (!trimmed) {
      toast.error('Name is required');
      return;
    }
    try {
      await api.put(`/user-management/permission-sets/${showEditPermSet.id}`, {
        name: trimmed,
        description: editPermSetForm.description?.trim() || null,
      });
      toast.success('Permission set updated');
      setShowEditPermSet(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permission set');
    }
  };

  // Clone a system permission set into an editable copy, then jump straight
  // into the Edit dialog on the fresh copy so the admin can rename it.
  const handleClonePermSet = async (setId) => {
    try {
      const res = await api.post(`/user-management/permission-sets/${setId}/clone`);
      toast.success(`Cloned as "${res.data.name}"`);
      await fetchData();
      // Auto-open Edit on the new copy so the admin can immediately rename it.
      openEditPermSet(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clone permission set');
    }
  };

  // Export users
  const handleExportUsers = async () => {
    try {
      const res = await api.get('/user-management/export/users');
      const blob = new Blob([res.data.csv_data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success(`Exported ${res.data.total_users} users`);
    } catch (error) {
      toast.error('Failed to export users');
    }
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = !userSearch || 
      u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.employee_id?.toLowerCase().includes(userSearch.toLowerCase());
    const matchesStatus = !userFilter.status || u.status === userFilter.status;
    const matchesRole = !userFilter.role || (u.roles || [u.role]).includes(userFilter.role);
    return matchesSearch && matchesStatus && matchesRole;
  });

  const getRoleColor = (role) => {
    const r = ROLES.find(r => r.value === role);
    return r?.color || 'bg-slate-800 text-slate-400 border-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="enterprise-user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <UserCog className="w-7 h-7 text-sky-400" />
            User Administration
          </h1>
          <p className="text-slate-400 mt-1">Manage users, security groups, and permissions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportUsers} data-testid="export-users-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => setShowCreateUser(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="create-user-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Users</p>
                  <p className="text-2xl font-bold text-white">{stats.total_users}</p>
                </div>
                <Users className="w-8 h-8 text-sky-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Active</p>
                  <p className="text-2xl font-bold text-emerald-400">{stats.active_users}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Pending</p>
                  <p className="text-2xl font-bold text-amber-400">{stats.pending_users}</p>
                </div>
                <Clock className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Security Groups</p>
                  <p className="text-2xl font-bold text-violet-400">{stats.total_security_groups}</p>
                </div>
                <Shield className="w-8 h-8 text-violet-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Permission Sets</p>
                  <p className="text-2xl font-bold text-cyan-400">{stats.total_permission_sets}</p>
                </div>
                <Key className="w-8 h-8 text-cyan-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="users" className="data-[state=active]:bg-slate-800">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="groups" className="data-[state=active]:bg-slate-800">
            <Shield className="w-4 h-4 mr-2" />
            Security Groups
          </TabsTrigger>
          <TabsTrigger value="permissions" className="data-[state=active]:bg-slate-800">
            <Key className="w-4 h-4 mr-2" />
            Permission Sets
          </TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-slate-800">
            <History className="w-4 h-4 mr-2" />
            Audit Log
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardContent className="p-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-4 mb-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input 
                    placeholder="Search users..." 
                    value={userSearch} 
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-10 bg-slate-950 border-slate-800"
                    data-testid="user-search-input"
                  />
                </div>
                <Select value={userFilter.status || "all"} onValueChange={(v) => setUserFilter({...userFilter, status: v === "all" ? "" : v})}>
                  <SelectTrigger className="w-[150px] bg-slate-950 border-slate-800">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="pending_activation">Pending</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={userFilter.role || "all"} onValueChange={(v) => setUserFilter({...userFilter, role: v === "all" ? "" : v})}>
                  <SelectTrigger className="w-[150px] bg-slate-950 border-slate-800">
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="all">All Roles</SelectItem>
                    {ROLES.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedUsers.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline">
                        Bulk Actions ({selectedUsers.length})
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-slate-900 border-slate-800">
                      <DropdownMenuItem onClick={() => handleBulkAction('activate')}>
                        <CheckCircle className="w-4 h-4 mr-2 text-emerald-400" />
                        Activate
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleBulkAction('deactivate')}>
                        <XCircle className="w-4 h-4 mr-2 text-red-400" />
                        Deactivate
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => {
                        const groupId = prompt('Enter Security Group ID:');
                        if (groupId) handleBulkAction('add_to_group', { group_id: groupId });
                      }}>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add to Group
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* Users Table */}
              <div className="rounded-md border border-slate-800 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="w-[30px]">
                        <Checkbox 
                          checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) setSelectedUsers(filteredUsers.map(u => u.id));
                            else setSelectedUsers([]);
                          }}
                        />
                      </TableHead>
                      <TableHead className="text-slate-400">User</TableHead>
                      <TableHead className="text-slate-400">Employee ID</TableHead>
                      <TableHead className="text-slate-400">Roles</TableHead>
                      <TableHead className="text-slate-400">Department</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Last Login</TableHead>
                      <TableHead className="text-slate-400 w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredUsers.map((user) => {
                        const userRoles = user.roles || [user.role];
                        const statusConfig = STATUS_CONFIG[user.status] || STATUS_CONFIG.active;
                        const StatusIcon = statusConfig.icon;
                        
                        return (
                          <TableRow key={user.id} className="border-slate-800 hover:bg-slate-800/50">
                            <TableCell>
                              <Checkbox 
                                checked={selectedUsers.includes(user.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) setSelectedUsers([...selectedUsers, user.id]);
                                  else setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-white">{user.name}</p>
                                <p className="text-sm text-slate-400">{user.email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-300">{user.employee_id || '-'}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {userRoles.slice(0, 2).map(role => (
                                  <Badge key={role} className={`${getRoleColor(role)} border text-xs capitalize`}>
                                    {role.replace('_', ' ')}
                                  </Badge>
                                ))}
                                {userRoles.length > 2 && (
                                  <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-xs">
                                    +{userRoles.length - 2}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-300">{user.department || '-'}</TableCell>
                            <TableCell>
                              <Badge className={`${statusConfig.color} border flex items-center gap-1 w-fit`}>
                                <StatusIcon className="w-3 h-3" />
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-slate-400 text-sm">
                              {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                                  <DropdownMenuItem onClick={() => setShowEditUser(user)}>
                                    <Edit className="w-4 h-4 mr-2" />
                                    Edit User
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleUserAction(user.id, 'reset-password')}>
                                    <Key className="w-4 h-4 mr-2" />
                                    Reset Password
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  {user.status === 'active' ? (
                                    <DropdownMenuItem onClick={() => handleUserAction(user.id, 'deactivate')} className="text-amber-400">
                                      <XCircle className="w-4 h-4 mr-2" />
                                      Deactivate
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem onClick={() => handleUserAction(user.id, 'activate')} className="text-emerald-400">
                                      <CheckCircle className="w-4 h-4 mr-2" />
                                      Activate
                                    </DropdownMenuItem>
                                  )}
                                  {user.status === 'locked' && (
                                    <DropdownMenuItem onClick={() => handleUserAction(user.id, 'unlock')} className="text-sky-400">
                                      <Unlock className="w-4 h-4 mr-2" />
                                      Unlock
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => handleUserAction(user.id, 'suspend')} className="text-red-400">
                                    <AlertTriangle className="w-4 h-4 mr-2" />
                                    Suspend
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Groups Tab */}
        <TabsContent value="groups" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <Button
              variant="outline"
              onClick={handleSyncFromRoles}
              data-testid="sync-from-roles-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync from roles
            </Button>
            <Button onClick={() => setShowCreateGroup(true)} className="bg-violet-600 hover:bg-violet-700" data-testid="create-group-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Security Group
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {securityGroups.map(group => (
              <Card key={group.id} className="bg-slate-900/60 border-slate-800">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-violet-400" />
                      <CardTitle className="text-lg">{group.name}</CardTitle>
                    </div>
                    {group.is_system && (
                      <Badge className="bg-slate-800 text-slate-400 border-slate-700">System</Badge>
                    )}
                  </div>
                  <CardDescription>{group.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">Members</span>
                      <span className="text-white font-medium">{group.member_count}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">Permission Sets</span>
                      <span className="text-white font-medium">{group.permission_set_ids?.length || 0}</span>
                    </div>
                    {group.auto_roles?.length > 0 && (
                      <div className="pt-1" data-testid={`group-auto-roles-${group.id}`}>
                        <div className="text-xs text-slate-500 mb-1">
                          Auto-populated by roles:
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {group.auto_roles.map(role => (
                            <Badge
                              key={role}
                              className="bg-violet-900/30 text-violet-300 border-violet-800 border text-xs capitalize"
                              title={`Any user assigned the "${role}" role is auto-added to this group.`}
                            >
                              {role.replace('_', ' ')}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={async () => {
                          const res = await api.get(`/user-management/security-groups/${group.id}`);
                          setShowGroupMembers(res.data);
                        }}
                      >
                        <Users className="w-4 h-4 mr-1" />
                        Members
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowEditGroup(group)}
                        disabled={group.is_system}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleDeleteGroup(group.id)}
                        disabled={group.is_system}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Permission Sets Tab */}
        <TabsContent value="permissions" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={openBulkAssign}
                data-testid="bulk-assign-btn"
              >
                <UserCog className="w-4 h-4 mr-2" />
                Bulk assign to users
              </Button>
              <Button
                variant="outline"
                onClick={openBulkHistory}
                data-testid="bulk-history-btn"
              >
                <History className="w-4 h-4 mr-2" />
                History &amp; undo
              </Button>
            </div>
            <Button onClick={() => setShowCreatePermSet(true)} className="bg-cyan-600 hover:bg-cyan-700" data-testid="create-permset-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Permission Set
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {permissionSets.map(ps => (
              <Card key={ps.id} className="bg-slate-900/60 border-slate-800">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Key className="w-5 h-5 text-cyan-400" />
                      <CardTitle className="text-lg">{ps.name}</CardTitle>
                    </div>
                    {ps.is_system && (
                      <Badge className="bg-slate-800 text-slate-400 border-slate-700">System</Badge>
                    )}
                  </div>
                  <CardDescription>{ps.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">Permissions</span>
                      <span className="text-white font-medium">{ps.permissions?.length || 0}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {[...new Set(ps.permissions?.map(p => p.module) || [])].slice(0, 5).map(module => (
                        <Badge key={module} className="bg-slate-800 text-slate-400 border-slate-700 text-xs">
                          {module}
                        </Badge>
                      ))}
                      {[...new Set(ps.permissions?.map(p => p.module) || [])].length > 5 && (
                        <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-xs">
                          +{[...new Set(ps.permissions?.map(p => p.module) || [])].length - 5}
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openEditPermSet(ps)}
                        data-testid={`edit-permset-${ps.id}`}
                        title={ps.is_system ? 'System set — only name & description are editable' : 'Edit name & description'}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      {ps.is_system && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleClonePermSet(ps.id)}
                          className="text-cyan-300 border-cyan-800 hover:bg-cyan-900/20"
                          data-testid={`clone-permset-${ps.id}`}
                          title="Create an editable copy (recommended — preserves the system baseline)"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Clone
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowViewPermSet(ps)}
                        data-testid={`view-permset-${ps.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleDeletePermSet(ps.id)}
                        disabled={ps.is_system}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`delete-permset-${ps.id}`}
                        title={ps.is_system ? 'System sets are read-only baselines — Clone to create an editable copy' : 'Delete permission set'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit" className="mt-4">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-amber-400" />
                User Management Audit Trail
              </CardTitle>
              <div className="flex items-center justify-between mt-1">
                <CardDescription>Track all user-related actions and changes</CardDescription>
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={auditActorOnly}
                    onChange={(e) => setAuditActorOnly(e.target.checked)}
                    className="accent-amber-500"
                    data-testid="audit-my-changes-toggle"
                  />
                  Show only my changes
                </label>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {auditLogs.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No audit logs found</p>
                  ) : (
                    auditLogs.map(log => (
                      <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                        <div className={`p-2 rounded-full ${
                          log.action.includes('created') ? 'bg-emerald-900/30 text-emerald-400' :
                          log.action.includes('deleted') || log.action.includes('suspended') ? 'bg-red-900/30 text-red-400' :
                          log.action.includes('activated') || log.action.includes('unlocked') ? 'bg-sky-900/30 text-sky-400' :
                          'bg-amber-900/30 text-amber-400'
                        }`}>
                          <ClipboardList className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium capitalize">
                            {log.action.replace(/_/g, ' ')}
                          </p>
                          <p className="text-sm text-slate-400">
                            {log.actor_email && `By ${log.actor_email}`}
                            {log.target_user_email && ` → ${log.target_user_email}`}
                          </p>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <p className="text-xs text-slate-500 mt-1">
                              {JSON.stringify(log.details).slice(0, 100)}...
                            </p>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-sky-400" />
              Create New User
            </DialogTitle>
            <DialogDescription>Add a new user to the system</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email *</Label>
                <Input 
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="mt-1 bg-slate-950 border-slate-800"
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <Label>Full Name *</Label>
                <Input 
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  className="mt-1 bg-slate-950 border-slate-800"
                  placeholder="John Doe"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Employee ID</Label>
                <Input 
                  value={newUser.employee_id}
                  onChange={(e) => setNewUser({...newUser, employee_id: e.target.value})}
                  className="mt-1 bg-slate-950 border-slate-800"
                  placeholder="EMP-001"
                />
              </div>
              <div>
                <Label>Job Title</Label>
                <Input 
                  value={newUser.job_title}
                  onChange={(e) => setNewUser({...newUser, job_title: e.target.value})}
                  className="mt-1 bg-slate-950 border-slate-800"
                  placeholder="Safety Officer"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <Input 
                  value={newUser.department}
                  onChange={(e) => setNewUser({...newUser, department: e.target.value})}
                  className="mt-1 bg-slate-950 border-slate-800"
                  placeholder="Flight Operations"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={newUser.phone}
                  onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                  className="mt-1 bg-slate-950 border-slate-800"
                  placeholder="+966 5X XXX XXXX"
                />
              </div>
            </div>
            <div>
              <Label>Roles</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {ROLES.map(role => (
                  <Badge 
                    key={role.value}
                    className={`cursor-pointer ${newUser.roles.includes(role.value) ? role.color : 'bg-slate-800 text-slate-500 border-slate-700'} border`}
                    onClick={() => {
                      if (newUser.roles.includes(role.value)) {
                        setNewUser({...newUser, roles: newUser.roles.filter(r => r !== role.value)});
                      } else {
                        setNewUser({...newUser, roles: [...newUser.roles, role.value]});
                      }
                    }}
                  >
                    {role.label}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="send_invite"
                checked={newUser.send_invite}
                onCheckedChange={(checked) => setNewUser({...newUser, send_invite: checked})}
              />
              <Label htmlFor="send_invite" className="cursor-pointer">Send activation invite email</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} className="bg-sky-600 hover:bg-sky-700">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      {showEditUser && (
        <Dialog open={!!showEditUser} onOpenChange={() => setShowEditUser(null)}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-amber-400" />
                Edit User: {showEditUser.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="rounded-md border border-slate-800 bg-slate-950/60 px-3 py-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Username (Email)</Label>
                <p className="text-slate-200 font-mono text-sm mt-0.5" data-testid="edit-user-email">
                  {showEditUser.email}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input 
                    value={showEditUser.name}
                    onChange={(e) => setShowEditUser({...showEditUser, name: e.target.value})}
                    className="mt-1 bg-slate-950 border-slate-800"
                    data-testid="edit-user-name"
                  />
                </div>
                <div>
                  <Label>Employee ID</Label>
                  <Input 
                    value={showEditUser.employee_id || ''}
                    onChange={(e) => setShowEditUser({...showEditUser, employee_id: e.target.value})}
                    className="mt-1 bg-slate-950 border-slate-800"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Job Title</Label>
                  <Input 
                    value={showEditUser.job_title || ''}
                    onChange={(e) => setShowEditUser({...showEditUser, job_title: e.target.value})}
                    className="mt-1 bg-slate-950 border-slate-800"
                  />
                </div>
                <div>
                  <Label>Department</Label>
                  <Input 
                    value={showEditUser.department || ''}
                    onChange={(e) => setShowEditUser({...showEditUser, department: e.target.value})}
                    className="mt-1 bg-slate-950 border-slate-800"
                  />
                </div>
              </div>
              <div>
                <Label>Roles</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {ROLES.map(role => {
                    const userRoles = showEditUser.roles || [showEditUser.role];
                    return (
                      <Badge 
                        key={role.value}
                        className={`cursor-pointer ${userRoles.includes(role.value) ? role.color : 'bg-slate-800 text-slate-500 border-slate-700'} border`}
                        onClick={() => {
                          if (userRoles.includes(role.value)) {
                            setShowEditUser({...showEditUser, roles: userRoles.filter(r => r !== role.value)});
                          } else {
                            setShowEditUser({...showEditUser, roles: [...userRoles, role.value]});
                          }
                        }}
                      >
                        {role.label}
                      </Badge>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <Select 
                  value={showEditUser.status || 'active'} 
                  onValueChange={(v) => setShowEditUser({...showEditUser, status: v})}
                >
                  <SelectTrigger className="mt-1 bg-slate-950 border-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditUser(null)}>Cancel</Button>
              <Button onClick={() => handleUpdateUser(showEditUser.id, showEditUser)} className="bg-amber-600 hover:bg-amber-700">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Security Group Dialog */}
      <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-violet-400" />
              Create Security Group
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Group Name *</Label>
              <Input 
                value={newGroup.name}
                onChange={(e) => setNewGroup({...newGroup, name: e.target.value})}
                className="mt-1 bg-slate-950 border-slate-800"
                placeholder="e.g., Flight Crew Supervisors"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={newGroup.description}
                onChange={(e) => setNewGroup({...newGroup, description: e.target.value})}
                className="mt-1 bg-slate-950 border-slate-800"
                placeholder="Describe this group's purpose..."
              />
            </div>
            <div>
              <Label>Permission Sets</Label>
              <div className="flex flex-wrap gap-2 mt-2 max-h-32 overflow-y-auto">
                {permissionSets.map(ps => (
                  <Badge 
                    key={ps.id}
                    className={`cursor-pointer ${newGroup.permission_set_ids.includes(ps.id) ? 'bg-cyan-900/30 text-cyan-400 border-cyan-800' : 'bg-slate-800 text-slate-500 border-slate-700'} border`}
                    onClick={() => {
                      if (newGroup.permission_set_ids.includes(ps.id)) {
                        setNewGroup({...newGroup, permission_set_ids: newGroup.permission_set_ids.filter(id => id !== ps.id)});
                      } else {
                        setNewGroup({...newGroup, permission_set_ids: [...newGroup.permission_set_ids, ps.id]});
                      }
                    }}
                  >
                    {ps.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateGroup(false)}>Cancel</Button>
            <Button onClick={handleCreateGroup} className="bg-violet-600 hover:bg-violet-700">Create Group</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Members Dialog */}
      {showGroupMembers && (
        <Dialog open={!!showGroupMembers} onOpenChange={() => setShowGroupMembers(null)}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-violet-400" />
                {showGroupMembers.name} - Members
              </DialogTitle>
              <DialogDescription>{showGroupMembers.description}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex gap-2">
                <Select onValueChange={(userId) => handleAddGroupMember(showGroupMembers.id, userId)}>
                  <SelectTrigger className="bg-slate-950 border-slate-800">
                    <SelectValue placeholder="Add member..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 max-h-48">
                    {users.filter(u => !showGroupMembers.members?.some(m => m.id === u.id)).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {showGroupMembers.members?.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No members in this group</p>
                  ) : (
                    showGroupMembers.members?.map(member => (
                      <div key={member.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                        <div>
                          <p className="font-medium text-white">{member.name}</p>
                          <p className="text-sm text-slate-400">{member.email}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleRemoveGroupMember(showGroupMembers.id, member.id)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* View Permission Set Dialog */}
      {/* Bulk Assign History — list past batches and one-click undo */}
      {showBulkHistory && (
        <Dialog open={showBulkHistory} onOpenChange={() => setShowBulkHistory(false)}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl" data-testid="bulk-history-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-amber-400" />
                Bulk assign history
              </DialogTitle>
              <DialogDescription>
                Every past bulk assign — expand a row to see the per-user before/after, or Undo to invert the change.
              </DialogDescription>
            </DialogHeader>

            {bulkHistoryLoading ? (
              <div className="py-10 text-center text-slate-400 text-sm">Loading…</div>
            ) : bulkHistory.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">No bulk-assign batches recorded yet.</div>
            ) : (
              <ScrollArea className="h-[440px] pr-3">
                <div className="space-y-2">
                  {bulkHistory.map((batch) => {
                    const isExpanded = expandedBatchId === batch.batch_id;
                    return (
                      <div
                        key={batch.batch_id}
                        className={`rounded-md border ${batch.undone ? 'border-slate-800 bg-slate-800/20' : 'border-slate-700 bg-slate-800/40'}`}
                        data-testid={`bulk-history-row-${batch.batch_id}`}
                      >
                        <div className="flex items-center justify-between p-3 gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-slate-500">
                                {new Date(batch.timestamp).toLocaleString()}
                              </span>
                              <Badge className={`text-[10px] border ${batch.mode === 'add' ? 'bg-emerald-900/30 text-emerald-300 border-emerald-800' : 'bg-amber-900/30 text-amber-300 border-amber-800'}`}>
                                {batch.mode}
                              </Badge>
                              {batch.state === 'undone' ? (
                                <Badge className="text-[10px] bg-slate-800 text-slate-400 border-slate-700 border">
                                  UNDONE{batch.last_flip_at ? ' · ' + new Date(batch.last_flip_at).toLocaleDateString() : ''}
                                </Badge>
                              ) : (batch.undo_count > 0 || batch.redo_count > 0) ? (
                                <Badge className="text-[10px] bg-cyan-900/30 text-cyan-300 border-cyan-800 border">
                                  APPLIED · flipped {(batch.undo_count || 0) + (batch.redo_count || 0)}×
                                </Badge>
                              ) : null}
                            </div>
                            <div className="text-sm text-slate-200 mt-1 truncate">
                              {batch.users_count} user{batch.users_count === 1 ? '' : 's'}
                              {' · '}
                              {batch.assigned_names.slice(0, 3).join(', ')}
                              {batch.assigned_names.length > 3 && ` +${batch.assigned_names.length - 3}`}
                            </div>
                            <div className="text-xs text-slate-500 truncate">
                              by {batch.actor_email || batch.actor_id}
                              {batch.last_flip_kind && batch.last_flip_by && (
                                <> · last {batch.last_flip_kind} by {batch.last_flip_by}</>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setExpandedBatchId(isExpanded ? null : batch.batch_id)}
                              data-testid={`bulk-history-expand-${batch.batch_id}`}
                            >
                              {isExpanded ? 'Hide' : 'Details'}
                            </Button>
                            {batch.state === 'undone' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleFlipBatch(batch.batch_id, 'redo')}
                                className="text-emerald-400 hover:text-emerald-300"
                                data-testid={`bulk-history-redo-${batch.batch_id}`}
                                title="Re-apply this batch — restores every affected user to the assigned state"
                              >
                                <RefreshCw className="w-3.5 h-3.5 mr-1" />
                                Redo
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleFlipBatch(batch.batch_id, 'undo')}
                                className="text-red-400 hover:text-red-300"
                                data-testid={`bulk-history-undo-${batch.batch_id}`}
                                title="Invert this batch — restores every affected user to their pre-batch permission-set list"
                              >
                                <RefreshCw className="w-3.5 h-3.5 mr-1" />
                                Undo
                              </Button>
                            )}
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="border-t border-slate-700/60 p-3 space-y-1">
                            {batch.users.map((u) => (
                              <div key={u.user_id} className="text-xs flex items-center gap-2 py-1">
                                <span className="text-slate-300 flex-1 truncate">{u.user_email || u.user_id}</span>
                                <span className="text-slate-500">before: {(u.before || []).length}</span>
                                <span className="text-slate-500">→</span>
                                <span className="text-cyan-300">after: {(u.after || []).length}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkHistory(false)} data-testid="bulk-history-close">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk Assign Permission Sets to multiple users */}
      {showBulkAssign && (
        <Dialog open={showBulkAssign} onOpenChange={() => setShowBulkAssign(false)}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl" data-testid="bulk-assign-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCog className="w-5 h-5 text-cyan-400" />
                Bulk assign permission sets
              </DialogTitle>
              <DialogDescription>
                Pick the sets and the users — every mutation is audited with a before/after list of set ids.
              </DialogDescription>
            </DialogHeader>

            {bulkPreview ? (
              /* PREVIEW STEP — per-user diff of what would change */
              <div className="space-y-3" data-testid="bulk-preview-panel">
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div className="rounded-md bg-slate-800/70 p-2">
                    <div className="text-2xl font-semibold text-cyan-300">{bulkPreview.summary.users_changed}</div>
                    <div className="text-xs text-slate-400">Users would change</div>
                  </div>
                  <div className="rounded-md bg-slate-800/70 p-2">
                    <div className="text-2xl font-semibold text-slate-300">{bulkPreview.summary.users_unchanged}</div>
                    <div className="text-xs text-slate-400">Unchanged</div>
                  </div>
                  <div className="rounded-md bg-slate-800/70 p-2">
                    <div className="text-2xl font-semibold text-emerald-400">+{bulkPreview.summary.total_added_permissions}</div>
                    <div className="text-xs text-slate-400">Permissions gained</div>
                  </div>
                  <div className="rounded-md bg-slate-800/70 p-2">
                    <div className="text-2xl font-semibold text-red-400">−{bulkPreview.summary.total_removed_permissions}</div>
                    <div className="text-xs text-slate-400">Permissions lost</div>
                  </div>
                </div>

                <ScrollArea className="h-[340px] border border-slate-800 rounded-lg p-2">
                  <div className="space-y-2">
                    {bulkPreview.users.map((u) => {
                      const changed = u.added_permissions.length + u.removed_permissions.length > 0;
                      return (
                        <div
                          key={u.user_id}
                          className={`p-2 rounded-md border ${changed ? 'border-cyan-800/70 bg-cyan-900/10' : 'border-slate-800 bg-slate-800/30'}`}
                          data-testid={`bulk-preview-row-${u.user_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="min-w-0">
                              <div className="text-sm text-slate-100 truncate">{u.name || u.email || u.user_id}</div>
                              <div className="text-xs text-slate-500 truncate">{u.email}</div>
                            </div>
                            {u.not_found ? (
                              <Badge className="bg-red-900/30 text-red-300 border-red-800 border text-xs">Not found</Badge>
                            ) : !changed ? (
                              <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-xs">No change</Badge>
                            ) : (
                              <div className="text-xs text-slate-400 shrink-0">
                                <span className="text-emerald-400">+{u.added_permissions.length}</span>
                                {' / '}
                                <span className="text-red-400">−{u.removed_permissions.length}</span>
                              </div>
                            )}
                          </div>
                          {changed && (
                            <div className="mt-2 space-y-1">
                              {u.added_permissions.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  <span className="text-[10px] uppercase tracking-wide text-emerald-500 mr-1">Gained</span>
                                  {u.added_permissions.slice(0, 24).map((p, i) => (
                                    <Badge key={i} className="bg-emerald-900/30 text-emerald-300 border-emerald-800 border text-[10px]">
                                      {p.module}·{p.action}
                                    </Badge>
                                  ))}
                                  {u.added_permissions.length > 24 && (
                                    <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[10px]">
                                      +{u.added_permissions.length - 24} more
                                    </Badge>
                                  )}
                                </div>
                              )}
                              {u.removed_permissions.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  <span className="text-[10px] uppercase tracking-wide text-red-500 mr-1">Lost</span>
                                  {u.removed_permissions.slice(0, 24).map((p, i) => (
                                    <Badge key={i} className="bg-red-900/30 text-red-300 border-red-800 border text-[10px]">
                                      {p.module}·{p.action}
                                    </Badge>
                                  ))}
                                  {u.removed_permissions.length > 24 && (
                                    <Badge className="bg-slate-800 text-slate-400 border-slate-700 text-[10px]">
                                      +{u.removed_permissions.length - 24} more
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            ) : (
            /* PICK STEP — sets + users + mode */
            <>
            <div className="grid grid-cols-2 gap-4">
              {/* Left column: pick permission sets */}
              <div>
                <Label className="mb-2 block">Permission sets ({bulkAssignForm.permission_set_ids.length}/{permissionSets.length})</Label>
                <ScrollArea className="h-[260px] border border-slate-800 rounded-lg p-2">
                  <div className="space-y-1">
                    {permissionSets.map(ps => {
                      const isOn = bulkAssignForm.permission_set_ids.includes(ps.id);
                      return (
                        <label
                          key={ps.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${isOn ? 'bg-cyan-900/30 text-cyan-100' : 'text-slate-300 hover:bg-slate-800/50'}`}
                          data-testid={`bulk-ps-${ps.id}`}
                        >
                          <input
                            type="checkbox"
                            className="accent-cyan-500"
                            checked={isOn}
                            onChange={() => {
                              const next = isOn
                                ? bulkAssignForm.permission_set_ids.filter(x => x !== ps.id)
                                : [...bulkAssignForm.permission_set_ids, ps.id];
                              setBulkAssignForm({ ...bulkAssignForm, permission_set_ids: next });
                            }}
                          />
                          <span className="flex-1 truncate">{ps.name}</span>
                          {ps.is_system && <Badge className="bg-slate-800 text-slate-500 border-slate-700 text-[10px]">System</Badge>}
                        </label>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>

              {/* Right column: pick users (with search) */}
              <div>
                <Label className="mb-2 block">Users ({bulkAssignForm.user_ids.length}/{users.length})</Label>
                <Input
                  placeholder="Search by name or email…"
                  className="bg-slate-800 border-slate-700 mb-2"
                  value={bulkAssignForm.userSearch}
                  onChange={(e) => setBulkAssignForm({ ...bulkAssignForm, userSearch: e.target.value })}
                  data-testid="bulk-user-search"
                />
                <ScrollArea className="h-[220px] border border-slate-800 rounded-lg p-2">
                  <div className="space-y-1">
                    {users
                      .filter(u => {
                        const q = bulkAssignForm.userSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (u.email || '').toLowerCase().includes(q)
                            || (u.first_name || '').toLowerCase().includes(q)
                            || (u.last_name  || '').toLowerCase().includes(q)
                            || (u.name       || '').toLowerCase().includes(q);
                      })
                      .map(u => {
                        const isOn = bulkAssignForm.user_ids.includes(u.id);
                        return (
                          <label
                            key={u.id}
                            className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${isOn ? 'bg-cyan-900/30 text-cyan-100' : 'text-slate-300 hover:bg-slate-800/50'}`}
                            data-testid={`bulk-user-${u.id}`}
                          >
                            <input
                              type="checkbox"
                              className="accent-cyan-500"
                              checked={isOn}
                              onChange={() => {
                                const next = isOn
                                  ? bulkAssignForm.user_ids.filter(x => x !== u.id)
                                  : [...bulkAssignForm.user_ids, u.id];
                                setBulkAssignForm({ ...bulkAssignForm, user_ids: next });
                              }}
                            />
                            <div className="flex-1 truncate">
                              <div className="text-slate-100">{u.first_name || u.name || ''} {u.last_name || ''}</div>
                              <div className="text-xs text-slate-500 truncate">{u.email}</div>
                            </div>
                          </label>
                        );
                      })}
                  </div>
                </ScrollArea>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-2">
              <Label className="text-sm">Mode:</Label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  className="accent-cyan-500"
                  checked={bulkAssignForm.mode === 'add'}
                  onChange={() => setBulkAssignForm({ ...bulkAssignForm, mode: 'add' })}
                  data-testid="bulk-mode-add"
                />
                <span>Add to existing</span>
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input
                  type="radio"
                  className="accent-cyan-500"
                  checked={bulkAssignForm.mode === 'replace'}
                  onChange={() => setBulkAssignForm({ ...bulkAssignForm, mode: 'replace' })}
                  data-testid="bulk-mode-replace"
                />
                <span>Replace</span>
              </label>
              <div className="ml-auto text-xs text-slate-500">
                Audit-logged as `bulk_permission_set_assign`
              </div>
            </div>
            </>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setShowBulkAssign(false); setBulkPreview(null); }} data-testid="bulk-assign-cancel">
                Cancel
              </Button>
              {!bulkPreview ? (
                <Button
                  onClick={handleFetchBulkPreview}
                  className="bg-slate-700 hover:bg-slate-600"
                  data-testid="bulk-preview-btn"
                  disabled={bulkAssignForm.permission_set_ids.length === 0 || bulkAssignForm.user_ids.length === 0 || bulkPreviewLoading}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  {bulkPreviewLoading ? 'Computing preview…' : 'Preview changes'}
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setBulkPreview(null)}
                    data-testid="bulk-preview-back"
                  >
                    ← Back to picks
                  </Button>
                  <Button
                    onClick={handleBulkAssign}
                    className="bg-cyan-600 hover:bg-cyan-700"
                    data-testid="bulk-assign-confirm"
                    disabled={bulkPreview.summary.users_changed === 0}
                  >
                    Apply to {bulkPreview.summary.users_changed} user{bulkPreview.summary.users_changed === 1 ? '' : 's'}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* View Permission Set (read-only permissions overview) */}
      {showViewPermSet && (
        <Dialog open={!!showViewPermSet} onOpenChange={() => setShowViewPermSet(null)}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-cyan-400" />
                {showViewPermSet.name}
              </DialogTitle>
              <DialogDescription>{showViewPermSet.description}</DialogDescription>
            </DialogHeader>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {PERMISSION_MODULES.map(module => {
                  const modulePerms = showViewPermSet.permissions?.filter(p => p.module === module) || [];
                  if (modulePerms.length === 0) return null;
                  
                  return (
                    <div key={module} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                      <h4 className="font-medium text-white capitalize mb-2">{module.replace('_', ' ')}</h4>
                      <div className="flex flex-wrap gap-2">
                        {modulePerms.map((perm, idx) => (
                          <Badge key={idx} className="bg-cyan-900/30 text-cyan-400 border-cyan-800 border">
                            {perm.action} ({perm.scope})
                          </Badge>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Permission Set (metadata only — name + description). Permissions
          grid and is_system flag stay read-only by design (option B). */}
      {showEditPermSet && (
        <Dialog open={!!showEditPermSet} onOpenChange={() => setShowEditPermSet(null)}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="edit-permset-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-cyan-400" />
                Edit Permission Set
              </DialogTitle>
              <DialogDescription>
                Update the name and description. The permission grid is locked to preserve access-control history — delete and recreate if permissions must change.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="edit-permset-name">Name</Label>
                <Input
                  id="edit-permset-name"
                  value={editPermSetForm.name}
                  onChange={(e) => setEditPermSetForm({ ...editPermSetForm, name: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  data-testid="edit-permset-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-permset-description">Description</Label>
                <Input
                  id="edit-permset-description"
                  value={editPermSetForm.description}
                  onChange={(e) => setEditPermSetForm({ ...editPermSetForm, description: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  data-testid="edit-permset-description"
                />
              </div>
              <div className="text-xs text-slate-400 pt-1">
                {showEditPermSet.permissions?.length || 0} permission(s) attached · read-only from this dialog
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditPermSet(null)} data-testid="edit-permset-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleSavePermSetEdit}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="edit-permset-save"
              >
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Permission Set Dialog */}
      <Dialog open={showCreatePermSet} onOpenChange={setShowCreatePermSet}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-cyan-400" />
              Create Permission Set
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input 
                  value={newPermSet.name}
                  onChange={(e) => setNewPermSet({...newPermSet, name: e.target.value})}
                  className="mt-1 bg-slate-950 border-slate-800"
                  placeholder="e.g., Incident Reviewer"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input 
                  value={newPermSet.description}
                  onChange={(e) => setNewPermSet({...newPermSet, description: e.target.value})}
                  className="mt-1 bg-slate-950 border-slate-800"
                  placeholder="Describe this permission set..."
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Permissions</Label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400" data-testid="permset-selected-count">
                    {newPermSet.permissions.length} / {PERMISSION_MODULES.length * PERMISSION_ACTIONS.length} selected
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => {
                      const all = PERMISSION_MODULES.flatMap(m => PERMISSION_ACTIONS.map(a => ({ module: m, action: a, scope: 'organization' })));
                      setNewPermSet({ ...newPermSet, permissions: all });
                    }}
                    data-testid="permset-select-all"
                  >
                    Select all
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setNewPermSet({ ...newPermSet, permissions: [] })}
                    data-testid="permset-clear-all"
                  >
                    Clear all
                  </Button>
                </div>
              </div>
              <ScrollArea className="h-[300px] border border-slate-800 rounded-lg p-3">
                <div className="space-y-4">
                  {/* Column header — quick-select every module for a given action */}
                  <div className="flex items-center gap-2 pl-1 pb-2 border-b border-slate-800">
                    <div className="w-32 text-xs text-slate-500 shrink-0">Quick pick →</div>
                    <div className="flex flex-wrap gap-1">
                      {PERMISSION_ACTIONS.map(action => {
                        const total = PERMISSION_MODULES.length;
                        const selected = newPermSet.permissions.filter(p => p.action === action).length;
                        const allOn = selected === total;
                        return (
                          <Badge
                            key={action}
                            className={`cursor-pointer border text-xs ${allOn ? 'bg-cyan-700/40 text-cyan-200 border-cyan-600' : 'bg-slate-800 text-slate-400 border-slate-700'}`}
                            onClick={() => {
                              const others = newPermSet.permissions.filter(p => p.action !== action);
                              if (allOn) {
                                setNewPermSet({ ...newPermSet, permissions: others });
                              } else {
                                const additions = PERMISSION_MODULES.map(m => ({ module: m, action, scope: 'organization' }));
                                setNewPermSet({ ...newPermSet, permissions: [...others, ...additions] });
                              }
                            }}
                            data-testid={`permset-col-${action}`}
                          >
                            {action} ({selected}/{total})
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  {PERMISSION_MODULES.map(module => {
                    const modulePerms = newPermSet.permissions.filter(p => p.module === module);
                    const rowAllOn = modulePerms.length === PERMISSION_ACTIONS.length;
                    return (
                      <div key={module} className="p-3 rounded-lg bg-slate-800/30 border border-slate-700">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-white capitalize">{module.replace('_', ' ')}</h4>
                          <button
                            type="button"
                            className={`text-xs px-2 py-0.5 rounded border ${rowAllOn ? 'text-cyan-300 border-cyan-700 bg-cyan-900/30' : 'text-slate-400 border-slate-700 bg-slate-900/60 hover:text-slate-200'}`}
                            onClick={() => {
                              const others = newPermSet.permissions.filter(p => p.module !== module);
                              if (rowAllOn) {
                                setNewPermSet({ ...newPermSet, permissions: others });
                              } else {
                                const additions = PERMISSION_ACTIONS.map(a => ({ module, action: a, scope: 'organization' }));
                                setNewPermSet({ ...newPermSet, permissions: [...others, ...additions] });
                              }
                            }}
                            data-testid={`permset-row-${module}`}
                          >
                            {rowAllOn ? 'Clear row' : 'All actions'}
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {PERMISSION_ACTIONS.map(action => {
                            const isSelected = modulePerms.some(p => p.action === action);
                            return (
                              <Badge 
                                key={action}
                                className={`cursor-pointer ${isSelected ? 'bg-cyan-900/30 text-cyan-400 border-cyan-800' : 'bg-slate-800 text-slate-500 border-slate-700'} border`}
                                onClick={() => {
                                  if (isSelected) {
                                    setNewPermSet({
                                      ...newPermSet, 
                                      permissions: newPermSet.permissions.filter(p => !(p.module === module && p.action === action))
                                    });
                                  } else {
                                    setNewPermSet({
                                      ...newPermSet, 
                                      permissions: [...newPermSet.permissions, { module, action, scope: 'organization' }]
                                    });
                                  }
                                }}
                              >
                                {action}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePermSet(false)}>Cancel</Button>
            <Button onClick={handleCreatePermSet} className="bg-cyan-600 hover:bg-cyan-700">
              Create Permission Set
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
